import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { useWaitlist } from "@/hooks/useWaitlist";
import { trackWaitlistView } from "@/lib/analytics";
import { COPY } from "@/content/copy";

const CTASection = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const { signup } = useWaitlist();

  // Track waitlist view on mount
  useEffect(() => {
    trackWaitlistView('cta');
  }, []);

  const emailOk = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!emailOk(email)) {
      toast.warning("Please check your email", {
        description: "That doesn't look like a valid email address.",
      });
      return;
    }
    setLoading(true);
    const res = await signup(email, {
      source: "cta",
      path: window.location.pathname,
    });
    setLoading(false);

    if (res.ok) {
      if (res.status === "created") {
        toast.success("You're on the waitlist 🎉", {
          description: "We'll email you as soon as early access opens in your region.",
        });
        setEmail("");
      } else {
        toast("You're already on the list", {
          description: "We'll keep you posted with updates.",
        });
      }
    } else {
      toast.error("Something went wrong", {
        description: (res as { ok: false; error: string }).error,
      });
    }
  };

  return (
    <section className="py-20 px-4 bg-white relative overflow-hidden">
      {/* Subtle background decoration */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5" aria-hidden="true"></div>
      <div className="relative max-w-4xl mx-auto text-center">
        <h2 className="text-4xl md:text-6xl font-heading font-bold mb-6 gradient-text">
          {COPY.closing.title}
        </h2>
        <p className="text-xl text-foreground/80 font-body mb-12 max-w-2xl mx-auto leading-relaxed">
          {COPY.closing.description}
        </p>
        
        <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
          <Input
            type="email"
            placeholder="Enter your email…"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="flex-1 h-12 text-base"
            required
          />
          <Button 
            type="submit" 
            variant="gradient" 
            size="lg" 
            className="whitespace-nowrap"
            disabled={loading}
          >
            {loading ? "Joining..." : COPY.closing.cta}
          </Button>
        </form>
        
        <p className="text-sm text-muted-foreground mt-6 font-body">
          No spam, just updates on when Flexaura launches.
        </p>
        
        {/* On success: scroll to top */}
        <div className="mt-4">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="text-xs"
          >
            Back to Top
          </Button>
        </div>
      </div>
    </section>
  );
};

export default CTASection;